import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Edit, Eye, EyeOff, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ForumCategoryCardProps {
  id: string;
  title: string;
  slug: string;
  description: string;
  icon: React.ReactNode;
  topicsCount: number;
  commentsCount: number;
  lastActivity: string;
  latestTopics: { title: string; link: string }[];
  color: string;
  isHidden: boolean;
  isHighlighted: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleHidden?: () => void;
  onToggleHighlight?: () => void;
}

const ForumCategoryCard: React.FC<ForumCategoryCardProps> = ({
  title,
  slug, // Ezt a prop-ot fogjuk használni a linkhez!
  description,
  icon,
  topicsCount,
  commentsCount,
  lastActivity,
  latestTopics,
  color,
  isHidden,
  isHighlighted,
  onEdit,
  onDelete,
  onToggleHidden,
  onToggleHighlight,
}) => {
  // A helyes link mostantól a backend által biztosított 'slug'-ból készül.
  const categoryLink = `/forum/category/${slug}`;

  // ÚJ: Háttérszín osztályok a fejlécnek
  const headerBgClasses: { [key: string]: string } = {
    blue: 'bg-blue-500/10',
    green: 'bg-green-500/10',
    purple: 'bg-purple-500/10',
    red: 'bg-red-500/10',
    yellow: 'bg-yellow-500/10',
    gray: 'bg-gray-500/10',
  };

  // ÚJ: Szövegszín osztályok az ikonnak, hogy kontrasztos legyen a háttérrel
  const iconTextClasses: { [key: string]: string } = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    red: 'text-red-500',
    yellow: 'text-yellow-500',
    gray: 'text-gray-500',
  };

  return (
    <Card className={`relative flex flex-col transition-all hover:shadow-lg ${isHighlighted ? 'ring-2 ring-primary' : ''} ${isHidden ? 'opacity-50 grayscale' : 'bg-card'}`}>
      {isHidden && (
        <div className="absolute top-2 right-2 z-10 rounded-full bg-yellow-400 px-2 py-1 text-xs font-bold text-yellow-900">
          Elrejtve
        </div>
      )}
      {/* A kártya tartalma mostantól egy flex container, hogy a lábléc alulra kerüljön */}
      <div className="flex flex-grow flex-col">
        {/* A linkelt rész most magában foglalja a fejlécet és a tartalmat */}
        <a href={categoryLink} className="group">
          <CardHeader className={`flex flex-row items-center gap-4 p-6 ${headerBgClasses[color] || 'bg-gray-500/10'}`}>
            <div className={iconTextClasses[color] || 'text-gray-500'}>{icon}</div>
            <CardTitle className="text-xl group-hover:text-primary transition-colors">{title}</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{description}</p>
            <div className="text-xs text-muted-foreground flex justify-between">
              <span>Témák: {topicsCount}</span>
              <span>Hozzászólások: {commentsCount}</span>
            </div>
          </CardContent>
        </a>
        {/* A lábléc most már a flex container aljára igazodik az `mt-auto`-val */}
        <CardFooter className="mt-auto flex flex-col items-start border-t p-6 pt-4 text-xs text-muted-foreground">
          <div className="w-full mb-2">
            <strong className="block mb-1">Utolsó aktivitás:</strong>
            <p className="truncate">{lastActivity}</p>
          </div>
          {latestTopics.length > 0 && (
            <div className="w-full mt-2">
              <strong className="block mb-1">Legújabb témák:</strong>
              <ul className="list-none pl-0 space-y-1">
                {latestTopics.map((topic, index) => (
                  <li key={index} className="truncate"><a href={topic.link} className="hover:underline hover:text-primary">{topic.title}</a></li>
                ))}
              </ul>
            </div>
          )}
          {(onEdit || onDelete || onToggleHidden || onToggleHighlight) && (
            <div className="w-full flex justify-end gap-2 border-t pt-4 mt-4">
              {onEdit && <Button variant="ghost" size="icon" onClick={onEdit}><Edit size={16} /></Button>}
              {onToggleHighlight && <Button variant="ghost" size="icon" onClick={onToggleHighlight} className={isHighlighted ? 'text-primary' : ''}>{isHighlighted ? <Star size={16} fill="currentColor" /> : <Star size={16} />}</Button>}
              {onToggleHidden && <Button variant="ghost" size="icon" onClick={onToggleHidden}>{isHidden ? <EyeOff size={16} /> : <Eye size={16} />}</Button>}
              {onDelete && <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={onDelete}><Trash2 size={16} /></Button>}
            </div>
          )}
        </CardFooter>
      </div>
    </Card>
  );
};

export default ForumCategoryCard;